import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'react-toastify';
import Iconify from 'src/components/iconify';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import moment from 'moment';
import 'moment/locale/tr'; // Türkçe tarih formatları için eklendi
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const DAY_MAP_TR = { "Monday": "P.tesi", "Tuesday": "Salı", "Wednesday": "Çarş.", "Thursday": "Perş.", "Friday": "Cuma", "Saturday": "C.tesi", "Sunday": "Pazar" };
const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const zeroOutSeries = (series) => {
  if (!Array.isArray(series)) return [];
  const firstEl = series[0];
  if (typeof firstEl === 'number') { return series.map(() => 0); }
  if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
    return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
  }
  return [];
};

export default function MallManagementSafetyLocker() {
  const theme = useTheme();
  moment.locale('tr'); 

  const [filterText, setFilterText] = useState('');
  const [startDate, setStartDate] = useState(moment().subtract(7, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [analyticsData, setAnalyticsData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [originalTableData, setOriginalTableData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [dailyUsage, setDailyUsage] = useState(0);
  const [monthlyAvarageUsage, setMonthlyAvarageUsage] = useState(0);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [heatmapDataType, setHeatmapDataType] = useState('usageCount');
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);

  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  const [priceInfo, setPriceInfo] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [activePriceTab, setActivePriceTab] = useState(0);

  const { hasPermission: safetyLockerPermission, loading: permissionLoading } = useServicePermission(SERVICES.SAFETY_LOCKER);

  const mallID = localStorage.getItem("mallID");
  const token = localStorage.getItem('tokenForService');
  const parsedUser = JSON.parse(localStorage.getItem('user'));

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);
  const handleOpenImageModal = () => setImageModalOpen(true);
  const handleCloseImageModal = () => setImageModalOpen(false);
  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Emanet Dolap',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  const fetchPriceInfo = useCallback(async () => {
    if (!token || !mallID) {
      setLoadingPrices(false);
      return;
    }

    setLoadingPrices(true);
    try {
      const mallInfoResponse = await Services.GetMallInformation(token, mallID);

      if (!mallInfoResponse.data.status || !mallInfoResponse.data.result.boxList) {
        setPriceInfo([]); 
        setLoadingPrices(false);
        return;
      }

      const boxList = mallInfoResponse.data.result.boxList;
      if (boxList.length === 0) {
        setPriceInfo([]);
        setLoadingPrices(false);
        return;
      }

      const locationSettingsPromises = boxList.map(locationCode =>
        Services.GetLocationSettings(locationCode)
      );
      const locationSettingsResponses = await Promise.all(locationSettingsPromises);

      const processedPriceInfo = locationSettingsResponses
        .filter(res => res.data.status && res.data.result) 
        .map(res => {
          const { locationCode, locationName, durations } = res.data.result;

          const activePrices = durations
            .filter(d => d.status === true && d.price != null)
            .map(d => ({ time: d.time, price: d.price })); 

          return { locationCode, locationName, prices: activePrices };
        });

      setPriceInfo(processedPriceInfo);

    } catch (error) {
      console.error("Fiyat bilgisi alınırken hata:", error);
      toast.error('Fiyat bilgileri alınamadı.');
      setPriceInfo([]);
    } finally {
      setLoadingPrices(false);
    }
  }, [token, mallID]);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      if (!token || !mallID) return;
      const response = await Services.GetLiveAnalyticsDropbox(token, mallID);
      if (response.data.status && response.data.result) {
        setAnalyticsData(response.data.result);
        setMonthlyAvarageUsage(response.data.result.monthlyAvarageUsage);
        setDailyUsage(response.data.result.dailyUsage);
        const initialList = response.data.result.dropboxList ? Object.values(response.data.result.dropboxList).flat() : [];
        setTableData(initialList);
        setOriginalTableData(initialList);
      }
    } catch (error) {
      toast.error('Analitik verileri alınırken bir hata oluştu.');
      setAnalyticsData({});
      setTableData([]);
      setOriginalTableData([]);
    }
  }, [token, mallID]);

  const fetchFilteredList = useCallback(async () => {
    if (!hasPermission) return;
    setLoadingTable(true);
    try {
      if (!token || !mallID) return;
      const response = await Services.GetLiveAnalyticsDropboxList(token, mallID, startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
      if (response.data.status && response.data.result) {
        const filteredList = response.data.result ? Object.values(response.data.result).flat() : [];
        setTableData(filteredList);
      }
    } catch (error) {
      toast.error('Liste verisi alınırken bir hata oluştu.');
    } finally {
      setLoadingTable(false);
    }
  }, [token, mallID, startDate, endDate, hasPermission]);

  const handleExportExcel = () => {
    if (!hasPermission || tableData.length === 0) {
      toast.warn('Export edilecek veri bulunamadı.');
      return;
    }

    const exportData = tableData.map((row, index) => ({
      'id': index + 1, 
      'lokasyon': row.locationCode || '',
      'dolapNo': row.lockerNumber || '',
      'baslangic': row.startDate
        ? moment(row.startDate).format('DD.MM.YYYY HH:mm:ss')
        : '-',
      'bitis': row.endDate
        ? moment(row.endDate).format('DD.MM.YYYY HH:mm:ss')
        : '-',
      'fiyat': row.price ? `${row.price} ₺` : '0 ₺',
      'islemTipi': row.orderType === 1 ? 'Yeni Kiralama' : 'Uzatma',
    }));

    const sheetTitle = `Emanet_Dolap_Listesi_${moment().format('YYYY-MM-DD')}`;

    api.post('excel/export', {
      type: 'excel',
      content: exportData,
      header: {
        'No': 'number',
        'Lokasyon': 'string',
        'Dolap No': 'string',
        'Başlangıç': 'string',
        'Bitiş': 'string',
        'Fiyat': 'string',
        'İşlem Tipi': 'string',
      },
      sheetTitle: sheetTitle
    })
      .then(response => {
        if (response.data.status) {
          window.open(response.data.result, '_blank');
          toast.success('Excel dosyası başarıyla oluşturuldu.');
        } else {
          toast.error(response.data.message || 'Excel dosyası oluşturulurken bir hata oluştu.');
        }
      })
      .catch(error => {
        console.error('Excel export hatası:', error);
        toast.error(error.message || 'Excel dosyası oluşturulurken bir hata oluştu.');
      });
  };

  useEffect(() => {
    if (permissionLoading) {
      return;
    }

    const checkPermissionAndFetchData = async () => {
      setLoadingAnalytics(true);
      if (!mallID || !token) {
        setLoadingAnalytics(false);
        setLoadingPrices(false)
        return;
      }
      try {
        const userHasPermission = safetyLockerPermission;
        setHasPermission(userHasPermission);

        if (userHasPermission) {
          await Promise.all([
            fetchAnalyticsData(),
            fetchPriceInfo()
          ]);
        } else {
          setShowApplyBanner(true);
          setAnalyticsData({});
          setTableData([]);
          setPriceInfo([]);
          setLoadingPrices(false);
        }
      } catch (err) {
        console.error("İzin kontrolü sırasında hata:", err);
        setAnalyticsData({});
        setTableData([]);
        setPriceInfo([]);
        setLoadingPrices(false);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    checkPermissionAndFetchData();
  }, [mallID, token, fetchAnalyticsData, fetchPriceInfo, safetyLockerPermission, permissionLoading]);

  const processedData = useMemo(() => {
    const data = analyticsData || {};
    const { inUseCount = 0, totalBoxCount = 0, monthlyHourlyUsageTotalCount = {}, weeklyHourlyUsageTotalCount = {}, dayHourlyUsageTotalCount = {}, yearlyUsageTotalCount = {} } = data;

    const occupancyRate = totalBoxCount > 0 ? ((inUseCount / totalBoxCount) * 100).toFixed(1) : 0;

    const normalizedDayHourlyData = {};
    if (dayHourlyUsageTotalCount) {
      for (const key in dayHourlyUsageTotalCount) {
        normalizedDayHourlyData[key.toLowerCase()] = dayHourlyUsageTotalCount[key];
      }
    }

    const extendDataByDayAndTime = {};
    DAY_ORDER.forEach(day => { extendDataByDayAndTime[day.toLowerCase()] = {}; });

    originalTableData.forEach(item => {
      if (item.orderType !== 1) {
        const startDate = moment(item.startDate);
        const dayKey = startDate.locale('en').format('dddd').toLowerCase();
        const hourSlot = `${startDate.format('HH')}:00-${startDate.clone().add(1, 'hour').format('HH')}:00`;

        if (extendDataByDayAndTime[dayKey]) {
          if (!extendDataByDayAndTime[dayKey][hourSlot]) {
            extendDataByDayAndTime[dayKey][hourSlot] = 0;
          }
          extendDataByDayAndTime[dayKey][hourSlot] += 1;
        }
      }
    });

    const dayAvgHourUsageChartData = DAY_ORDER.map(day => {
      const dayData = normalizedDayHourlyData[day.toLowerCase()] || {};
      let totalUsage = 0;
      let weightedHourSum = 0;
      Object.entries(dayData).forEach(([hourRange, values]) => {
        const startHour = parseInt(hourRange.split(':')[0], 10);
        const usageCount = values.usageCount || 0;
        if (usageCount > 0) {
          totalUsage += usageCount;
          weightedHourSum += startHour * usageCount;
        }
      });
      return totalUsage > 0 ? weightedHourSum / totalUsage : null;
    });

    const dayUsageTotalCountData = DAY_ORDER.map(day => {
      const dayData = normalizedDayHourlyData[day.toLowerCase()] || {};
      return Object.values(dayData).reduce((sum, val) => sum + (val.usageCount || 0), 0);
    });

    const dayUsageChart = {
      series: [{ name: 'Emanet Dolap Kullanım Adedi', data: dayAvgHourUsageChartData }],
      options: {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: 'smooth', width: 3 },
        colors: ['#2563EB'],
        markers: { size: 5 },
        xaxis: { categories: Object.values(DAY_MAP_TR), axisBorder: { show: false } },
        yaxis: {
          labels: {
            formatter: (val) => {
              const hour = Math.floor(val);
              if (isNaN(hour) || hour < 0) return '';
              return `${hour}:00-${hour + 1}:00`;
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value, { dataPointIndex }) => {
              const totalUsage = dayUsageTotalCountData[dataPointIndex];
              return `${totalUsage} adet`;
            },
            title: {
              formatter: (seriesName, { dataPointIndex }) => Object.values(DAY_MAP_TR)[dataPointIndex]
            }
          }
        },
        legend: { show: false }
      }
    };

    const monthlyUsage = Object.entries(monthlyHourlyUsageTotalCount).reduce((acc, [weekRange, hours]) => {
      if (!hours || typeof hours !== 'object') return acc;
      const { usage, revenue } = Object.values(hours).reduce((weekAcc, hour) => ({ usage: weekAcc.usage + (hour.usageCount || 0), revenue: weekAcc.revenue + (hour.totalPrice || 0) }), { usage: 0, revenue: 0 });
      const weekLabel = weekRange.replace('-rest', `-${moment().endOf('month').format('DD')}`);
      acc.categories.push(`${weekLabel}`);
      acc.usageData.push(usage);
      acc.revenueData.push(revenue);
      return acc;
    }, { categories: [], usageData: [], revenueData: [] });

    const monthlyUsageChart = {
      series: [
        { name: 'Ciro', type: 'column', data: monthlyUsage.revenueData },
        { name: 'Kullanım Adedi', type: 'line', data: monthlyUsage.usageData },
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '40%' } },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: { categories: monthlyUsage.categories },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          },
          {
            opposite: true,
            title: { text: 'Adet' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      }
    };

    const yearlyUsage = Object.entries(yearlyUsageTotalCount || {})
      .sort((a, b) => a[0].localeCompare(b[0])) 
      .reduce((acc, [monthKey, data]) => {
        const monthLabel = moment(monthKey, 'YYYY-MM').format('MMM YYYY'); 
        acc.categories.push(monthLabel);
        acc.usageData.push(data.usageCount || 0);
        acc.revenueData.push(data.totalPrice || 0);
        return acc;
      }, { categories: [], usageData: [], revenueData: [] });

    const yearlyUsageChart = {
      series: [
        { name: 'Ciro', type: 'column', data: yearlyUsage.revenueData },
        { name: 'Kullanım Adedi', type: 'line', data: yearlyUsage.usageData },
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '40%' } },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: {
          categories: yearlyUsage.categories,
          labels: {
            rotate: -45,
            rotateAlways: false,
          }
        },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          },
          {
            opposite: true,
            title: { text: 'Adet' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      }
    };

    // --- YENİ EKLENEN: DİNAMİK GÜN ARALIĞI HESAPLAMA ---
    const todayMoment = moment();
    const currentDayOfMonth = todayMoment.date();
    let startD, endD;

    if (currentDayOfMonth <= 7) {
      startD = 1; endD = 7;
    } else if (currentDayOfMonth <= 14) {
      startD = 8; endD = 14;
    } else if (currentDayOfMonth <= 21) {
      startD = 15; endD = 21;
    } else {
      startD = 22; endD = todayMoment.daysInMonth();
    }

    const dynamicWeekDates = [];
    for (let d = startD; d <= endD; d++) {
      dynamicWeekDates.push(todayMoment.clone().date(d));
    }
    // --------------------------------------------------

    // --- Günlük Kullanım ve Ciro Hareketleri (Dinamik X Ekseni) ---
    const weeklyUsageForChart = dynamicWeekDates.reduce((acc, dateObj) => {
      const dateStr = dateObj.format('YYYY-MM-DD');
      const dayData = weeklyHourlyUsageTotalCount[dateStr] || {};

      let dailyUsage = 0;
      let dailyRevenue = 0;

      Object.values(dayData).forEach(hourSlotData => {
        dailyUsage += (hourSlotData.usageCount || 0);
        dailyRevenue += (hourSlotData.totalPrice || 0);
      });

      acc.categories.push(dateObj.format('DD MMMM')); // Örn: 01 Mart
      acc.usageData.push(dailyUsage);
      acc.revenueData.push(dailyRevenue);
      return acc;
    }, { categories: [], usageData: [], revenueData: [] });

    const weeklyUsageChart = {
      series: [
        { name: 'Ciro', type: 'column', data: weeklyUsageForChart.revenueData },
        { name: 'Kullanım Adedi', type: 'line', data: weeklyUsageForChart.usageData }
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: { categories: weeklyUsageForChart.categories },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          },
          {
            opposite: true,
            title: { text: 'Kullanım Adedi' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      }
    };

    // --- Günlük Kullanım ve Uzatmalar (Dinamik X Ekseni) ---
    const weeklyTimeSlotData = dynamicWeekDates.reduce((acc, dateObj) => {
      const dateStr = dateObj.format('YYYY-MM-DD');
      const dayData = weeklyHourlyUsageTotalCount[dateStr] || {};

      let dUsage = 0;
      let dExtend = 0;

      Object.values(dayData).forEach(hourSlotData => {
        dUsage += (hourSlotData.usageCount || 0);
        dExtend += (hourSlotData.extendCount || 0); // Veride extendCount var, direkt buradan alıyoruz
      });

      acc.usage.push(dUsage);
      acc.extend.push(dExtend);
      acc.categories.push(dateObj.format('DD MMMM')); 
      return acc;
    }, { usage: [], extend: [], categories: [] });

    const weeklyTimeSlotChart = {
      series: [{ name: 'Kullanım Adedi', data: weeklyTimeSlotData.usage }, { name: 'Uzatma Adedi', data: weeklyTimeSlotData.extend }],
      options: { 
        chart: { type: 'bar', stacked: true, toolbar: { show: false } }, 
        plotOptions: { bar: { columnWidth: '60%' } }, 
        colors: ['#1C64F2', '#FDBA8C'], 
        xaxis: { categories: weeklyTimeSlotData.categories }, 
        legend: { position: 'bottom' } 
      }
    };

    const weeklyHeatmapChart = (() => {
      const data = weeklyHourlyUsageTotalCount || {};
      if (Object.keys(data).length === 0) { return { series: [], options: {}, maxCount: 10, colors: [] }; }

      const dataByDayName = {};
      for (const dateStr in data) { const dayKey = moment(dateStr).locale('en').format('dddd'); dataByDayName[dayKey] = data[dateStr]; }

      const oneHourSlots = Array.from({ length: 12 }, (_, i) => `${10 + i}:00-${11 + i}:00`);

      const series = oneHourSlots.map(timeSlot => ({
        name: timeSlot.split('-')[0],
        data: DAY_ORDER.map(dayKey => ({
          x: DAY_MAP_TR[dayKey],
          y: (dataByDayName[dayKey] || {})[timeSlot]?.[timeSlot]?.usageCount || 0
        }))
      })).reverse();

      const allCounts = Object.values(dataByDayName)
        .flatMap(dayData => Object.values(dayData))
        .flatMap(timeSlotOuter => Object.values(timeSlotOuter))
        .map(timeSlotInner => timeSlotInner.usageCount || 0);

      let maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 0;
      if (maxCount <= 0) {
        maxCount = 10;
      }

      const colors = ['#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];

      const ranges = [{ from: 0, to: 0, color: colors[0] }]; 

      if (maxCount === 1) {
        ranges.push({ from: 1, to: 1, color: colors[3] }); 
      } else if (maxCount > 1) {
        const step1 = Math.max(1, Math.floor(maxCount * 0.25));
        const step2 = Math.max(step1 + 1, Math.floor(maxCount * 0.7));

        ranges.push({ from: 1, to: step1, color: colors[1] });
        ranges.push({ from: step1 + 1, to: step2, color: colors[2] });
        ranges.push({ from: step2 + 1, to: maxCount, color: colors[3] });
      }

      const options = {
        chart: { type: 'heatmap', toolbar: { show: false } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.4,
            radius: 2,
            colorScale: {
              ranges: ranges
            }
          }
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 3, colors: ['#fff'] },
        xaxis: { type: 'category' },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => `${value} adet kullanım` } }
      };

      return { series, options, maxCount, colors };
    })();

    const dailyHeatmapChart = (() => {
      const data = heatmapDataType === 'usageCount'
        ? normalizedDayHourlyData
        : extendDataByDayAndTime;

      if (Object.keys(data).length === 0) { return { series: [], options: {}, maxCount: 10, colors: [] }; }
      const oneHourSlots = Array.from({ length: 12 }, (_, i) => `${10 + i}:00-${11 + i}:00`);

      const hourlyTotals = oneHourSlots.map(timeSlot => {
        return DAY_ORDER.reduce((sum, dayKey) => {
          let yValue = 0;
          if (heatmapDataType === 'usageCount') {
            yValue = data[dayKey.toLowerCase()]?.[timeSlot]?.['usageCount'] || 0;
          } else {
            yValue = data[dayKey.toLowerCase()]?.[timeSlot] || 0;
          }
          return sum + yValue;
        }, 0);
      });

      const dailyTotals = DAY_ORDER.map(dayKey => {
        return oneHourSlots.reduce((sum, timeSlot) => {
          let yValue = 0;
          if (heatmapDataType === 'usageCount') {
            yValue = data[dayKey.toLowerCase()]?.[timeSlot]?.['usageCount'] || 0;
          } else {
            yValue = data[dayKey.toLowerCase()]?.[timeSlot] || 0;
          }
          return sum + yValue;
        }, 0);
      });

      const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

      const totalRow = {
        name: 'Toplam',
        data: [
          ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
          { x: 'Toplam', y: grandTotal }
        ]
      };

      const series = [
        totalRow,
        ...oneHourSlots.map((timeSlot, index) => ({
          name: timeSlot.split('-')[0],
          data: [
            ...DAY_ORDER.map(dayKey => {
              let yValue = 0;
              if (heatmapDataType === 'usageCount') {
                yValue = data[dayKey.toLowerCase()]?.[timeSlot]?.['usageCount'] || 0;
              } else {
                yValue = data[dayKey.toLowerCase()]?.[timeSlot] || 0;
              }
              return { x: DAY_MAP_TR[dayKey], y: yValue };
            }),
            { x: 'Toplam', y: hourlyTotals[index] }
          ]
        })).reverse()
      ];

      let allCounts = [];
      if (heatmapDataType === 'usageCount') {
        allCounts = Object.values(data)
          .flatMap(day => Object.values(day))
          .map(item => item['usageCount'] || 0);
      } else {
        allCounts = Object.values(data)
          .flatMap(day => Object.values(day));
      }

      const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
      const usageColors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
      const extendColors = ['#D7D7D7', '#89D6CE', '#54C4B8', '#14B8A6', '#188B7E'];
      const activeColors = heatmapDataType === 'usageCount' ? usageColors : extendColors;
      const options = {
        chart: { type: 'heatmap', toolbar: { show: false } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.4,
            radius: 2,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: activeColors[0] },
                { from: 1, to: Math.floor(maxCount * 0.25), color: activeColors[2] },
                { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: activeColors[3] },
                { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: activeColors[4] }
              ]
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#000'],
            fontSize: '12px',
            fontWeight: 500
          }
        },
        stroke: { show: true, width: 3, colors: ['#fff'] },
        xaxis: { type: 'category' },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => `${value} adet` } }
      };
      return { series, options, maxCount, colors: activeColors };
    })();

    return { inUseCount, totalBoxCount, occupancyRate, dayUsageChart, monthlyUsageChart, yearlyUsageChart, weeklyUsageChart, weeklyTimeSlotChart, weeklyHeatmapChart, dailyHeatmapChart };
  }, [analyticsData, heatmapDataType, tableData, originalTableData]);

  const tableRows = useMemo(() => {
    return tableData.filter(item =>
      (item.nameSurName && item.nameSurName.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.gsmNumber && item.gsmNumber.includes(filterText))
    );
  }, [tableData, filterText]);
  const paginatedRows = tableRows.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage);

  if (loadingAnalytics || permissionLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  if (!analyticsData && !hasPermission) return <Box sx={{ p: 3 }}><Typography>Veri bulunamadı.</Typography></Box>;

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
      <Helmet><title>Emanet Dolap</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Emanet Dolap</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="text" startIcon={<Iconify icon="eva:image-outline" />} sx={{ textTransform: 'none' }} onClick={handleOpenImageModal}>Tanıtım Görselleri</Button>
          <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}><Card elevation={0} sx={{ p: 2, display: 'flex', alignItems: 'center', minHeight: 170 }}><Box flexGrow={1}><Typography variant="subtitle2">Göz Adedi</Typography><Typography variant="body2" color="text.secondary" mt={1}>Toplam: <strong>{processedData.totalBoxCount ?? 0}</strong></Typography><Typography variant="body2" color="text.secondary">Kullanımda: <strong>{processedData.inUseCount ?? 0}</strong></Typography></Box><Iconify icon="eva:layers-outline" width={40} sx={{ p: 1, borderRadius: 1, color: '#fff', background: 'linear-gradient(135deg, #3461D6 0%, #264697 100%)' }} /></Card></Grid>
        <Grid item xs={12} md={3}><Card elevation={0} sx={{ p: 2, display: 'flex', alignItems: 'center', minHeight: 170 }}><Box flexGrow={1}><Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography><Typography variant="body2" color="text.secondary" mt={1}>Toplam: <strong>{hasPermission ? dailyUsage : 0}</strong></Typography><Typography variant="body2" color="text.secondary">Ortalama: <strong>{hasPermission ? monthlyAvarageUsage : 0}</strong></Typography></Box><Iconify icon="eva:layers-outline" width={40} sx={{ p: 1, borderRadius: 1, color: '#fff', background: 'linear-gradient(135deg, #3461D6 0%, #264697 100%)' }} /></Card></Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={0} sx={{ p: 2, display: 'flex', alignItems: 'center', minHeight: 170 }}>
            <Box flexGrow={1}>
              <Typography variant="subtitle2">Doluluk Oranı</Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>Oran: <strong>%{hasPermission ? processedData.occupancyRate : 0}</strong></Typography>
            </Box>
            <Box width={80} height={80}>
              <ReactApexChart options={{ chart: { sparkline: { enabled: true } }, plotOptions: { pie: { donut: { size: '70%' } } }, colors: ['#3150A0', '#F7A937'], labels: ['Kullanımda', 'Boşta'] }} series={hasPermission ? [parseFloat(processedData.occupancyRate), 100 - parseFloat(processedData.occupancyRate)] : [0, 100]} type="donut" height={80} />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={0} sx={{ p: 2, minHeight: 170, display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                mb: priceInfo.length > 1 ? 0 : 2,
                flexShrink: 0
              }}
            >
              <Typography variant="subtitle2">
                Fiyat Bilgileri
              </Typography>

              {!loadingPrices && hasPermission && priceInfo.length > 0 && (
                <Chip
                  label={`${priceInfo.length} Lokasyon`}
                  size="small"
                  sx={{
                    fontSize: '11px',
                    height: '20px',   
                    backgroundColor: (theme) => theme.palette.grey[200]
                  }}
                />
              )}
            </Stack>
            {loadingPrices && !loadingAnalytics && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loadingPrices && hasPermission && (
              <>
                {priceInfo.length > 1 && (
                  <Tabs
                    value={activePriceTab}
                    onChange={(e, newValue) => setActivePriceTab(newValue)}
                    variant="standard"
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      mb: 1,
                      minHeight: 'auto'
                    }}
                  >
                    {priceInfo.map((location, index) => (
                      <Tab
                        label={location.locationName || location.locationCode}
                        key={location.locationCode || index}
                        sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          fontSize: '12px',
                          pb: 0,
                          px: .5,
                        }}
                      />
                    ))}
                  </Tabs>
                )}

                {priceInfo[activePriceTab] && priceInfo[activePriceTab].prices.length > 0 ? (
                  <Box sx={{
                    mt: priceInfo.length > 1 ? 0.5 : 0,
                    flexGrow: 1
                  }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ minWidth: '45px', fontWeight: 600 }}>Saat:</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                        {priceInfo[activePriceTab].prices.map((p, i) => (
                          <Box key={i} sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 1, py: 0.25, textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{p.time}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ minWidth: '45px', fontWeight: 600 }}>Fiyat:</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                        {priceInfo[activePriceTab].prices.map((p, i) => (
                          <Box key={i} sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 1, py: 0.25, textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{p.price}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '60px' }}>
                    <Typography variant="body2" color="text.secondary">Aktif fiyat bulunamadı.</Typography>
                  </Box>
                )}
              </>
            )}

            {!loadingPrices && !hasPermission && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '60px' }}>
                <Typography variant="body2" color="text.secondary">-</Typography>
              </Box>
            )}

          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>Gün Ve Saat Bazlı Haftalık Kullanım Hareketleri</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Saat Aralığı</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ReactApexChart options={processedData.weeklyHeatmapChart.options} series={hasPermission ? processedData.weeklyHeatmapChart.series : zeroOutSeries(processedData.weeklyHeatmapChart.series)} type="heatmap" height={300} />
              </Box>
              <HeatmapLegend type="usageCount" maxCount={processedData.weeklyHeatmapChart.maxCount} colors={processedData.weeklyHeatmapChart.colors} label={'Emanet Dolap Kullanım Adedi'} />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Haftalık Kullanım ve Ciro Hareketleri</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={processedData.monthlyUsageChart.options} series={hasPermission ? processedData.monthlyUsageChart.series : zeroOutSeries(processedData.monthlyUsageChart.series)} type="line" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Günlük Kullanım ve Uzatmalar</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Adet</Typography>
            <ReactApexChart options={processedData.weeklyTimeSlotChart.options} series={hasPermission ? processedData.weeklyTimeSlotChart.series : zeroOutSeries(processedData.weeklyTimeSlotChart.series)} type="bar" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Günlük Kullanım ve Ciro Hareketleri</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={processedData.weeklyUsageChart.options} series={hasPermission ? processedData.weeklyUsageChart.series : zeroOutSeries(processedData.weeklyUsageChart.series)} type="line" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Aylık Kullanım ve Ciro Hareketleri (Son 12 Ay)</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={processedData.yearlyUsageChart.options} series={hasPermission ? processedData.yearlyUsageChart.series : zeroOutSeries(processedData.yearlyUsageChart.series)} type="line" height={280} />
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, height: 400 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Typography variant="h6">Gün ve Saat Bazlı Aylık Kullanım Hareketleri</Typography>
                <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Saat Aralığı</Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Veri Tipi</InputLabel>
                <Select value={heatmapDataType} label="Veri Tipi" onChange={(e) => setHeatmapDataType(e.target.value)}>
                  <MenuItem value="usageCount">Kullanım Adedi</MenuItem>
                  <MenuItem value="extendCount">Uzatma Adedi</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ReactApexChart options={processedData.dailyHeatmapChart.options} series={hasPermission ? processedData.dailyHeatmapChart.series : zeroOutSeries(processedData.dailyHeatmapChart.series)} type="heatmap" height={300} />
              </Box>
              <HeatmapLegend type={heatmapDataType} maxCount={processedData.dailyHeatmapChart.maxCount} colors={processedData.dailyHeatmapChart.colors} label={heatmapDataType === "usageCount" ? "Kullanım Adedi" : 'Uzatma Adedi'} />
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 4, p: 3, position: 'relative' }} elevation={0}>
        {loadingTable && <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 2 }}><CircularProgress /></Box>}
        <Typography variant="h6" gutterBottom>Dolap İşlemleri</Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><Button fullWidth variant="contained" size="medium" onClick={fetchFilteredList} disabled={loadingTable || !hasPermission}>FİLTRELE</Button></Grid>
          <Grid item xs={6} md={2}><Button onClick={handleExportExcel} fullWidth variant="contained" color="info" size="medium" disabled={!hasPermission}>EXCEL’E AKTAR</Button></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow><TableCell>LOKASYON</TableCell><TableCell>DOLAP NO</TableCell><TableCell>BAŞLANGIÇ</TableCell><TableCell>BİTİŞ</TableCell><TableCell>FİYAT</TableCell><TableCell>İŞLEM TİPİ</TableCell></TableRow></TableHead>
            <TableBody>
              {paginatedRows.length > 0 ? paginatedRows.map((row, idx) => (
                <TableRow key={row.id || idx} hover>
                  <TableCell>{row.locationCode}</TableCell>
                  <TableCell>{row.lockerNumber}</TableCell>
                  <TableCell>{moment(row.startDate).format('DD.MM.YYYY HH:mm')}</TableCell>
                  <TableCell>{moment(row.endDate).format('DD.MM.YYYY HH:mm')}</TableCell>
                  <TableCell>{row.price} ₺</TableCell>
                  <TableCell>{row.orderType === 1 ? 'Yeni Kiralama' : 'Uzatma'}</TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={6} align="center">{!hasPermission ? 'Verileri görüntülemek için hizmete başvuruda bulunun.' : 'Filtreleme sonucunda veri bulunamadı.'}</TableCell></TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={8} align="center"><Stack spacing={2} direction="row" justifyContent="center"><Pagination count={Math.ceil(tableRows.length / rowsPerPage)} page={page} onChange={(e, value) => setPage(value)} shape="rounded" /></Stack></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/ep.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={isImageModalOpen} onClose={handleCloseImageModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Görselleri<IconButton aria-label="close" onClick={handleCloseImageModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers>
          <Box component="img" src="/assets/mallManagementImages/emanet-dolabi.png" alt="Emanet Dolabı Tanıtım Görseli" sx={{ width: '100%', height: 'auto', borderRadius: 1 }} />
        </DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}